import {
  Tabs,
  TabList,
  TabPanels,
  Tab as ChakraTab,
  TabPanel,
  Text,
  Icon,
  Box,
  Flex,
  Center,
  Heading
} from "@chakra-ui/react"
import { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { FaUserCircle, FaUserEdit } from "react-icons/fa"
import { FaMagnifyingGlass } from "react-icons/fa6"
import { IoChatbubblesOutline } from "react-icons/io5"
import { BlobBackground } from "src/components/common/BlobBackground"
import { SafeArea } from "src/components/common/SafeArea"
import useConfig from "src/hooks/useConfig"
import useSearchParams from "src/hooks/useSearchParams"
import { parseLocalStorage } from "src/util"
import { useIsMobileDevice } from "src/util/browser"
import UIGridHeader from "./components/UIGridHeader"
import useGridPageStyling from "./hooks/useGridPageStyling"
import AvatarGrid from "./Tabs/AvatarGrid"
import CreatePersona from "./Tabs/CreatePersona"
import { RecentChats } from "./Tabs/RecentChats"

const TabIndexMap = {
  EXPLORE: 0,
  CHAT: 1,
  PERSONA: 2,
  CREATE: 3
}

const AvatarTabNavigator = () => {
  const config = useConfig()
  const { t } = useTranslation()
  const isMobileDevice = useIsMobileDevice()
  const { backgroundColor } = useGridPageStyling()

  const params = useSearchParams<{ go_back: "journey" }>()
  const returnRoute = params.go_back
  const navigatedFromJourney = returnRoute === "journey"

  const [tabIndex, setTabIndex] = useState<number>(
    parseLocalStorage("avatarsTabIndex", 0)
  )
  const [showGenerateForm, setShowGenerateForm] = useState(false)
  const [headerOverride, setHeaderOverride] = useState<string | null>(null)
  const [backButtonAction, setBackButtonAction] = useState<
    (() => void) | undefined
  >(undefined)

  const hideForm = useCallback(() => setShowGenerateForm(false), [])

  useEffect(() => {
    localStorage.setItem("avatarsTabIndex", JSON.stringify(tabIndex))
  }, [tabIndex])

  useEffect(() => {
    setHeaderOverride(showGenerateForm ? "Set Photo" : null)
    setBackButtonAction(showGenerateForm ? () => hideForm : undefined)
  }, [showGenerateForm])

  if (!config.avatarsGrid && !navigatedFromJourney) {
    return (
      <Flex height={"100vh"}>
        <Center>
          <Heading>{t("Avatar grid disabled")}</Heading>
        </Center>
      </Flex>
    )
  }

  const tabPanelStyles = {
    padding: "0px",
    height: "100%",
    overflow: "auto",
    px: ["8px", "12px", "12px"]
  }

  // Add border style on desktop around bottom tab
  const tablistStyles = isMobileDevice
    ? {}
    : {
        borderTopRightRadius: "20px",
        borderTopLeftRadius: "20px",
        border: "1px solid white",
        borderBottom: "none",
        overflow: "clip"
      }

  const getHeader = (index: number) => {
    if (headerOverride != null) return headerOverride
    switch (tabIndex) {
      case TabIndexMap.EXPLORE:
        return ""
      case TabIndexMap.CHAT:
        return "Recent Chats"
      case TabIndexMap.PERSONA:
        return "Create Persona"
      case TabIndexMap.CREATE:
        return ""
    }
    return ""
  }

  return (
    <Box position="relative" width="100%" height="100%">
      <BlobBackground background={backgroundColor} />
      <SafeArea position="absolute">
        <Box height="100%" display="flex" flexDir="column">
          <UIGridHeader
            navigatedFromJourney={navigatedFromJourney}
            showSearchBox={tabIndex === TabIndexMap.EXPLORE}
            headerTitle={getHeader(tabIndex)}
            showBackButton={backButtonAction != null}
            customBackAction={backButtonAction}
          />

          <Tabs
            minHeight={isMobileDevice ? "initial" : "50px"} // needed for safari desktop
            width="100%"
            marginX="auto"
            height="fit-content"
            maxWidth="1400px"
            display="flex"
            flexDir="column"
            alignItems="center"
            overflow={"auto"}
            flexGrow={1}
            isFitted
            variant="unstyled"
            index={tabIndex}
            onChange={index => setTabIndex(index)}
            isLazy
          >
            <TabPanels flexGrow={1} overflow={"auto"}>
              <TabPanel {...tabPanelStyles}>
                <AvatarGrid />
              </TabPanel>

              <TabPanel {...tabPanelStyles}>
                <RecentChats />
              </TabPanel>

              <TabPanel {...tabPanelStyles}>
                <CreatePersona
                  showGenerateForm={showGenerateForm}
                  setShowGenerateForm={val => {
                    setShowGenerateForm(val)
                  }}
                />
              </TabPanel>

              <TabPanel {...tabPanelStyles}>
                <Flex h="100%" align="center" justify="center">
                  <Heading color="white">Coming Soon</Heading>
                </Flex>
              </TabPanel>
            </TabPanels>

            <TabList width="100%" {...tablistStyles}>
              <Tab title="Explore" icon={FaMagnifyingGlass} />
              <Tab title="Chats" icon={IoChatbubblesOutline} />
              <Tab title="Persona" icon={FaUserCircle} />
              <Tab title="Create" icon={FaUserEdit} />
            </TabList>
          </Tabs>
        </Box>
      </SafeArea>
    </Box>
  )
}

const Tab = ({ title, icon }: { title: string; icon: any }) => {
  const {
    panelBackgroundColor,
    panelTextColor,
    panelMouseoverBackgroundColor
  } = useGridPageStyling()

  const isMobileDevice = useIsMobileDevice()

  const styles = isMobileDevice
    ? {}
    : {
        bg: panelBackgroundColor,
        _hover: {
          bg: "black",
          color: panelMouseoverBackgroundColor
        },
        _selected: {
          bg: "black",
          color: panelMouseoverBackgroundColor
        }
      }

  return (
    <ChakraTab
      width="100%"
      height="52px"
      display="flex"
      lineHeight="1"
      fontWeight="medium"
      color={panelTextColor}
      borderBottom={"0px"}
      _selected={{
        color: panelMouseoverBackgroundColor,
        fontWeight: 600
      }}
      {...styles}
      _focus={{}}
    >
      <Flex
        flexDir={["column", "row"]}
        align={"center"}
        gap={["0.3rem", "0.5rem"]}
      >
        <Icon as={icon} />
        <Text fontSize={["14px", "18px", "18px"]}>{title}</Text>
      </Flex>
    </ChakraTab>
  )
}

export default AvatarTabNavigator
